import { useDebouncedState } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { WidgetCategoriesStats } from "~/api/Api";
import Switch from "~/components/switch/Switch";
import { CATEGORY_AUTOMATION_KIND } from "~/helpers/consts";
import {
  Category,
  mergeStatsWithCategories,
  toStatArray,
} from "~/pages/dashboard/onPage/OnPagePage";

import { apiClient } from "../../../api/ApiClient";
import { Icon } from "../../../assets/icons/Icon";
import NoData from "../../../components/containers/NoData";
import { formatNumber } from "../../../helpers/helpers";
import Button from "../../mantine/button/Button";
import Select from "../../mantine/select/Select";
import { PaginationConfig } from "../../ui/pagination/Pagination";
import Spinner from "../../ui/spinner/Spinner";
import { ApproveAllConfirmationModal } from "../modals/ApproveAllConfirmationModal/ApproveAllConfirmationModal";
import styles from "../styles.module.scss";
import CategoryAssetItems from "./CategoryAssetItems";
import CategoryPageItems from "./CategoryPageItems";
import { ApprovePayload, PageItem } from "./types";

export interface CategoryType {
  id: number;
  slug?: string;
  category_name: string;
  approved_appearances: number;
  appearances: number;
  category_type?: string;
}

interface OnPageProps {
  categories: Category[];
  isLoading: boolean;
  noData: boolean;
}

interface CategoriesByPageId {
  [slug: string]: WidgetCategoriesStats[string];
}

const OnPage = ({ categories, isLoading, noData }: OnPageProps) => {
  const [currentCategoryId, setCurrentCategoryId] = useState<
    number | undefined
  >(undefined);
  const [isApproveAllModalOpen, setIsApproveAllModalOpen] = useState(false);

  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();

  const currentCategory = useMemo(
    () =>
      currentCategoryId === 0
        ? null
        : (categories.find((c) => c.id === currentCategoryId) ?? null),
    [categories, currentCategoryId],
  );

  const [allData, setAllData] = useState<PageItem[]>([]);
  const [assetTypeData, setAssetTypeData] = useState<PageItem[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 20,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });
  const [responseApproveData, setResponseApproveData] = useState<{
    singleItemUpdate?: boolean;
    page_id?: number;
  }>({});

  const [categoryShowInactive, setCategoryShowInactive] = useState<
    Record<number, boolean>
  >({});
  const showInactive = currentCategoryId
    ? (categoryShowInactive[currentCategoryId] ?? false)
    : false;
  const toggleShowInactive = (next: boolean) => {
    if (!currentCategoryId) return;

    setCategoryShowInactive((prev) => ({ ...prev, [currentCategoryId]: next }));
  };

  const allPagesLoaded = pagination.currentPage === pagination.totalPages;
  const isPageCategory = currentCategory?.category_type === "page";
  const processedPagesRef = useRef<Record<number, boolean>>({});

  const [searchTerm, setSearchTerm] = useDebouncedState("", 500);
  const [inputValue, setInputValue] = useState<string>("");

  const [userRemovedSlash, setUserRemovedSlash] = useState(false);

  const [isLoadMoreClicked, setIsLoadMoreClicked] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const currentCategoryIdRef = useRef<number | undefined>(undefined);

  // set initial value for currentCategoryId
  useEffect(() => {
    if (currentCategoryId == null && categories.length > 0) {
      setCurrentCategoryId(categories[0].id);
    }
  }, [categories, currentCategoryId]);

  useEffect(() => {
    if (
      currentCategoryIdRef.current !== currentCategoryId &&
      abortControllerRef.current
    ) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    currentCategoryIdRef.current = currentCategoryId;
  }, [currentCategoryId]);

  const { data: categoriesByPageId } = useQuery({
    queryKey: ["categoriesByPageId", id, selectedPageId],
    queryFn: async () => {
      const response = await apiClient.sites.pagesWidgetCategoriesStatsDetail(
        Number(id),
        Number(selectedPageId),
      );

      return response.data?.["widget/categories_stats"];
    },
    enabled: !!selectedPageId,
  });

  const { data: searchPages, isLoading: isLoadingSearchPages } = useQuery({
    queryKey: ["search-pages", searchTerm],
    queryFn: async () => {
      const response = await apiClient.sites.pagesAutomatedDetail(
        Number(id),
        {
          "page[size]": 100,
          "page[number]": pagination.currentPage,
          q: searchTerm
            ? {
                // @ts-expect-error BE types - missing input field
                path_and_query_start: searchTerm,
              }
            : undefined,
        },
        {
          headers: {
            "Authorize-Member-Actions": "update",
            "Authorize-Collection-Actions": "update",
          },
        },
      );
      const resp = response.data?.page_automations || [];
      const pages = resp.map(
        (page: { path_and_query?: string; id: number }) => ({
          label: page.path_and_query || "",
          value: page.id.toString(),
        }),
      );

      return pages;
    },
    enabled: !!searchTerm,
  });

  const {
    data: pendingStatusesAndPermissionsPageType,
    isFetching: isFetchingPendingStatusesAndPermissionsPageType,
  } = useQuery({
    queryKey: ["pending-statuses-and-permissions", id],
    queryFn: async () => {
      try {
        const response =
          await apiClient.sites.widgetRecommendationActionsDetail(
            Number(id),
            {
              "page[size]": 100,
              "page[number]": pagination.currentPage,
              q: {
                category_id_eq: currentCategoryId,
                status_eq: "pending",
                variant_eq: "automation",
              },
            },
            {
              headers: {
                "Authorize-Member-Actions": "create",
                "Authorize-Collection-Actions": "create",
              },
            },
          );

        const pendingItems =
          response?.data?.["widget/recommendation_actions"] || [];
        const approvePermissions = // @ts-expect-error BE types
          response?.data?.authorizations?.collection.find(
            (item: string) => item === "create",
          );

        return {
          pendingItems,
          approvePermission: !!approvePermissions,
        };
      } catch (error) {
        const status =
          //@ts-expect-error BE types
          error?.response?.status ||
          (error && typeof error === "object" && "status" in error
            ? error.status
            : null);

        if (status === 403) {
          return { pendingItems: [], approvePermission: false, status: 403 };
        }

        throw error;
      }
    },
    retry: (failureCount, error) => {
      if (
        //@ts-expect-error BE types
        error?.status === 403 ||
        //@ts-expect-error BE types
        (error && typeof error === "object" && error.response?.status === 403)
      ) {
        return true;
      }

      return failureCount < 3;
    },
    retryDelay: (attemptIndex, error) => {
      if (
        //@ts-expect-error BE types
        error?.status === 403 ||
        //@ts-expect-error BE types
        (error && typeof error === "object" && error.response?.status === 403)
      ) {
        return 1000;
      }

      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
    refetchInterval: (query) => {
      if (
        query?.state?.data?.pendingItems &&
        query.state.data.pendingItems.length > 0
      ) {
        return 1000;
      }

      if (query?.state?.data?.status === 403) {
        return 1000;
      }

      return false;
    },
  });

  //AssetType
  const { isFetching: isFetchingAssetSuggestions } = useQuery<PageItem[]>({
    queryKey: [
      "asset-suggestions",
      currentCategoryId,
      pagination.currentPage,
      showInactive,
      selectedPageId,
    ],
    // @ts-expect-error BE types
    queryFn: async () => {
      const response = await apiClient.sites.widgetRecommendationsDetail2(
        Number(id),
        {
          "page[size]": 15,
          "page[number]": pagination.currentPage,
          q: {
            category_id_eq: currentCategoryId,
            ...(selectedPageId && {
              page_recommendations_page_id_eq: Number(selectedPageId),
            }),
            ...(showInactive ? { active_eq: false } : {}),
          },
        },
        {
          headers: {
            "Authorize-Member-Actions": "update",
            "Authorize-Collection-Actions": "update",
          },
        },
      );

      const recommendations = response.data?.["widget/recommendations"] || [];

      const { headers } = response;
      setPagination((prev: PaginationConfig) => ({
        ...prev,
        totalItems: Number(headers.get("x-pager-total-count") || 0),
        nextPage: Number(headers.get("x-pager-next-page") || 0),
        prevPage: Number(headers.get("x-pager-prev-page") || 0),
        totalPages: Number(headers.get("x-pager-total-pages") || 1),
      }));

      // @ts-expect-error BE types
      setAssetTypeData((prevData) => {
        if (pagination.currentPage === 1) {
          return recommendations;
        } else {
          return [...prevData, ...recommendations];
        }
      });

      return recommendations;
    },
    enabled: Boolean(!!id && !!currentCategoryId && !isPageCategory),
  });

  //pagesType
  const { isLoading: isPagesTypeLoading, isFetching: isFetchingPagesType } =
    useQuery<PageItem[]>({
      queryKey: [
        "pages-page-suggestions",
        currentCategoryId,
        pagination.currentPage,
        selectedPageId,
        showInactive,
      ],
      queryFn: async () => {
        const response = await apiClient.sites.pagesAutomatedDetail(
          Number(id),
          {
            "page[size]": 15,
            "page[number]": pagination.currentPage,
            q: {
              page_recommendations_category_id_eq: currentCategoryId,
              ...(selectedPageId && { id_eq: Number(selectedPageId) }),
              ...(showInactive ? { recommendations_active_eq: false } : {}),
            },
          },
          {
            headers: {
              "Authorize-Member-Actions": "update",
              "Authorize-Collection-Actions": "update",
            },
          },
        );

        const pages = response.data?.page_automations || [];

        const existingDataMap = allData.reduce(
          (acc, page) => {
            acc[page.id] = page;

            return acc;
          },
          {} as Record<number, PageItem>,
        );

        const basicPages = pages.map((page: PageItem) => {
          if (existingDataMap[page.id]?.details) {
            return { ...page, details: existingDataMap[page.id].details };
          }

          return { ...page };
        });

        if (pagination.currentPage === 1) {
          setAllData(basicPages);
        } else {
          setAllData((prevData) => [...prevData, ...basicPages]);
        }

        const { headers } = response;
        setPagination((prev: PaginationConfig) => ({
          ...prev,
          totalItems: Number(headers.get("x-pager-total-count")),
          nextPage: Number(headers.get("x-pager-next-page")),
          prevPage: Number(headers.get("x-pager-prev-page")),
          totalPages: Number(headers.get("x-pager-total-pages")),
        }));

        if (pages.length > 0 && pagination.currentPage === 1) {
          const firstPage = pages[0];

          try {
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();

            const detailsResponse =
              await apiClient.sites.pagesWidgetRecommendationsDetail(
                Number(id),
                firstPage.id,
                {
                  "page[size]": 100,
                  q: {
                    category_id_eq: currentCategoryId,
                    ...(showInactive ? { active_eq: false } : {}),
                  },
                },
                {
                  headers: {
                    "Authorize-Member-Actions": "update",
                    "Authorize-Collection-Actions": "update",
                  },
                  signal: abortControllerRef.current.signal,
                },
              );

            processedPagesRef.current[firstPage.id] = true;

            if (currentCategoryIdRef.current === currentCategoryId) {
              const enhancedPage = {
                ...firstPage,
                details:
                  detailsResponse?.data?.["widget/recommendations"] || [],
              };

              // @ts-expect-error BE types
              setAllData((prevData) =>
                prevData.map((p) => (p.id === firstPage.id ? enhancedPage : p)),
              );
            }
          } catch (error) {
            console.error(`Error fetching details for first page:`, error);
          }
        }

        return pages;
      },
      enabled: !!id && !!currentCategoryId && isPageCategory,
    });

  useEffect(() => {
    processedPagesRef.current = {};
    setAssetTypeData([]);
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  }, [currentCategoryId]);

  useEffect(() => {
    // interrupt pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    processedPagesRef.current = {};
    setAllData([]);
    setAssetTypeData([]);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [showInactive]);

  const handleCategoryClick = (category: Category): void => {
    if (currentCategoryId === category.id) return;

    setCurrentCategoryId(category.id);
  };

  const handleApproveAll = () => {
    if (!currentCategoryId) return;

    approve({
      kind: allApproved ? "unapprove" : "approve",
      variant: "automation",
      category_id: currentCategoryId,
    });
  };

  const bottomRef = useRef<HTMLDivElement>(null);

  const allApproved =
    currentCategory?.approved_appearances === currentCategory?.appearances;
  const {
    mutate: approve,
    mutateAsync: approveAsync,
    isPending: isApprovePending,
  } = useMutation<void, Error, ApprovePayload>({
    mutationFn: async (data) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { singleItemUpdate, ...dataWithoutSingleItemUpdate } = data;
      await apiClient.sites.widgetRecommendationActionsCreate(
        Number(id),
        dataWithoutSingleItemUpdate,
      );
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["pending-statuses-and-permissions", id],
      });

      // save to state so can be addressed inside handleProcessedRecommendationActions()
      setResponseApproveData(variables);
    },
    onError: (error) => {
      console.error("Error approving item:", error);
    },
  });

  // After widgetRecommendationActionsCreate() wait for the right moment
  // to handle categories and objects 'approve' state
  useEffect(() => {
    // wait for all pendingItems to change status
    if (
      !isFetchingPendingStatusesAndPermissionsPageType &&
      pendingStatusesAndPermissionsPageType &&
      pendingStatusesAndPermissionsPageType.pendingItems.length === 0 &&
      Object.keys(responseApproveData).length > 0
    ) {
      const handleProcessedRecommendationActions = async () => {
        if (
          responseApproveData.singleItemUpdate &&
          responseApproveData.page_id &&
          isPageCategory &&
          pagination.currentPage === 1
        ) {
          // Only use single item update for first page to avoid pagination issues
          const specificPageId = responseApproveData.page_id;
          if (currentCategoryIdRef.current !== currentCategoryId) {
            return;
          }

          try {
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();
            const response =
              await apiClient.sites.pagesWidgetRecommendationsDetail(
                Number(id),
                specificPageId,
                {
                  "page[size]": 100,
                  q: {
                    category_id_eq: currentCategoryId,
                    ...(showInactive ? { active_eq: false } : {}),
                  },
                },
                {
                  headers: {
                    "Authorize-Member-Actions": "update",
                    "Authorize-Collection-Actions": "update",
                  },
                  signal: abortControllerRef.current.signal,
                },
              );
            if (currentCategoryIdRef.current === currentCategoryId) {
              // @ts-expect-error BE types
              setAllData((prevData) =>
                prevData.map((p) =>
                  p.id === specificPageId
                    ? {
                        ...p,
                        details:
                          response?.data?.["widget/recommendations"] || [],
                      }
                    : p,
                ),
              );
            }

            await queryClient.refetchQueries({
              queryKey: ["pages-page-suggestions", currentCategoryId],
              type: "active",
            });
          } catch (error) {
            //@ts-expect-error BE types
            if (error.name !== "AbortError") {
              console.error(
                `Error fetching details for page ${specificPageId}:`,
                error,
              );
            }
          }
        } else {
          if (isPageCategory) {
            processedPagesRef.current = {};
            try {
              // If we're on page 2 or higher, we need to refetch all pages to update approval counts
              if (pagination.currentPage > 1) {
                const currentPageBackup = pagination.currentPage;
                setPagination((prev) => ({
                  ...prev,
                  currentPage: 1,
                }));

                queryClient.invalidateQueries({
                  queryKey: ["pages-page-suggestions", currentCategoryId],
                });

                const fetchAllPages = async () => {
                  await new Promise((resolve) => setTimeout(resolve, 300));

                  for (let page = 2; page <= currentPageBackup; page++) {
                    setPagination((prev) => ({
                      ...prev,
                      currentPage: page,
                    }));

                    await queryClient.refetchQueries({
                      queryKey: [
                        "pages-page-suggestions",
                        currentCategoryId,
                        page,
                      ],
                      exact: true,
                    });

                    await new Promise((resolve) => setTimeout(resolve, 100));
                  }
                };
                fetchAllPages();
              } else {
                queryClient.invalidateQueries({
                  queryKey: ["pages-page-suggestions", currentCategoryId],
                });
              }
            } catch (error) {
              console.error("Error refreshing page details:", error);
            }
          } else {
            if (pagination.currentPage > 1) {
              const currentPageBackup = pagination.currentPage;
              setPagination((prev) => ({
                ...prev,
                currentPage: 1,
              }));

              queryClient.invalidateQueries({
                queryKey: ["asset-suggestions", currentCategoryId],
              });

              const fetchAllPages = async () => {
                await new Promise((resolve) => setTimeout(resolve, 300));

                for (let page = 2; page <= currentPageBackup; page++) {
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: page,
                  }));

                  await queryClient.refetchQueries({
                    queryKey: ["asset-suggestions", currentCategoryId, page],
                    exact: true,
                  });

                  await new Promise((resolve) => setTimeout(resolve, 100));
                }
              };
              fetchAllPages();
            } else {
              queryClient.invalidateQueries({
                queryKey: ["asset-suggestions", currentCategoryId],
              });
            }
          }
        }

        queryClient.invalidateQueries({
          queryKey: ["onpage-categories-stats"],
        });
      };

      // always reset responseApproveData
      handleProcessedRecommendationActions().finally(() =>
        setResponseApproveData({}),
      );
    }
  }, [
    id,
    isFetchingPendingStatusesAndPermissionsPageType,
    Object.keys(responseApproveData).length,
    pendingStatusesAndPermissionsPageType?.pendingItems.length,
  ]);

  const { mutate: editPageSuggestion } = useMutation<
    void,
    Error,
    { id: number; final: string }
  >({
    mutationFn: async (data) => {
      await apiClient.sites.widgetRecommendationsPartialUpdate(
        Number(id),
        data.id,
        data,
      );
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pending-statuses-and-permissions", id],
      });

      if (isPageCategory) {
        processedPagesRef.current = {};
        try {
          queryClient.invalidateQueries({
            queryKey: ["pages-page-suggestions", currentCategoryId],
          });
        } catch (error) {
          console.error("Error refreshing page details:", error);
        }
      } else {
        if (pagination.currentPage > 1) {
          const currentPageBackup = pagination.currentPage;
          setPagination((prev) => ({
            ...prev,
            currentPage: 1,
          }));

          queryClient.invalidateQueries({
            queryKey: ["asset-suggestions", currentCategoryId],
          });
          const fetchAllPages = async () => {
            await new Promise((resolve) => setTimeout(resolve, 300));

            for (let page = 2; page <= currentPageBackup; page++) {
              setPagination((prev) => ({
                ...prev,
                currentPage: page,
              }));

              await queryClient.refetchQueries({
                queryKey: ["asset-suggestions", currentCategoryId, page],
                exact: true,
              });

              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          };
          fetchAllPages();
        } else {
          queryClient.invalidateQueries({
            queryKey: ["asset-suggestions", currentCategoryId],
          });
        }
      }
    },
  });

  const isAnyFetching =
    isFetchingPendingStatusesAndPermissionsPageType ||
    isFetchingAssetSuggestions ||
    isFetchingPagesType ||
    isApprovePending;

  const isInitialLoading = isLoading && pagination.currentPage === 1;

  useEffect(() => {
    if (!isAnyFetching && isLoadMoreClicked) {
      setIsLoadMoreClicked(false);
    }
  }, [isAnyFetching, isLoadMoreClicked]);

  const allCategoryItems =
    categories?.reduce(
      (sum, category) => sum + (category.appearances || 0),
      0,
    ) || 0;
  const approvedCategoryItems =
    categories?.reduce(
      (sum, category) => sum + (category.approved_appearances || 0),
      0,
    ) || 0;
  const allCategoryItemsApproved = allCategoryItems === approvedCategoryItems;

  const displayCategories: Category[] = useMemo(() => {
    if (selectedPageId && categoriesByPageId) {
      const categoriesObj = categoriesByPageId as
        | CategoriesByPageId
        | undefined;
      if (!categoriesObj) return [];

      const statArray = toStatArray(categoriesObj);

      return mergeStatsWithCategories(statArray, categories, {
        kind: CATEGORY_AUTOMATION_KIND,
      });
    }

    return categories as Category[];
  }, [selectedPageId, categoriesByPageId, categories]);

  // select the first category from the list when we change
  // categories source (global categories from props of from search)
  useEffect(() => {
    if (displayCategories.length === 0) return;

    const exists =
      currentCategoryId != null &&
      displayCategories.some((c) => c.id === currentCategoryId);

    // if currentCategory is null or is not in the list
    if (!exists) {
      setCurrentCategoryId(displayCategories[0].id);
    }
  }, [displayCategories, currentCategoryId]);

  const fetchPageDetails = async (pageId: number) => {
    if (processedPagesRef.current[pageId]) {
      return;
    }

    if (currentCategoryIdRef.current !== currentCategoryId) {
      return;
    }

    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const detailsResponse =
        await apiClient.sites.pagesWidgetRecommendationsDetail(
          Number(id),
          pageId,
          {
            "page[size]": 100,
            q: {
              category_id_eq: currentCategoryId,
              ...(showInactive ? { active_eq: false } : {}),
            },
          },
          {
            headers: {
              "Authorize-Member-Actions": "update",
              "Authorize-Collection-Actions": "update",
            },
            signal: abortControllerRef.current.signal,
          },
        );

      processedPagesRef.current[pageId] = true;

      if (currentCategoryIdRef.current === currentCategoryId) {
        const pageWithDetails = allData.find((p) => p.id === pageId);
        if (pageWithDetails) {
          const enhancedPage = {
            ...pageWithDetails,
            details: detailsResponse?.data?.["widget/recommendations"] || [],
          };

          // @ts-expect-error BE types
          setAllData((prevData) =>
            prevData.map((p) => (p.id === pageId ? enhancedPage : p)),
          );
        }
      }
    } catch (error) {
      console.error(`Error fetching details for page ${pageId}:`, error);
      throw error;
    }
  };
  const pendingApproveAll =
    isAnyFetching ||
    isApprovePending ||
    !!pendingStatusesAndPermissionsPageType?.pendingItems?.length;
  const hasAnyItems = allCategoryItems > 0;

  const onSearchChange = (value: string | null) => {
    if (inputValue.startsWith("/") && value && !value.startsWith("/")) {
      setUserRemovedSlash(true);
      setInputValue(value);
    } else if (value && !value.startsWith("/") && !userRemovedSlash) {
      setInputValue(`/${value}`);
    } else if (!value) {
      setUserRemovedSlash(false);
      setInputValue("");
      setSelectedPageId(null);
      setCurrentCategoryId(undefined);
    } else {
      setInputValue(value);
    }

    setSearchTerm(value ?? "");
  };

  return (
    <div className={styles.main}>
      <div className={styles.mainContent}>
        {isInitialLoading ? (
          <Spinner />
        ) : (
          <>
            <div className={styles.mainContentSidebar}>
              <div className={styles.pageListWrapper}>
                <div className={styles.pageList}>
                  <div className={styles.pageListHeader}>
                    <div>
                      <h2 className={styles.pageListTitle}>SEO Automations</h2>
                      {hasAnyItems && (
                        <div className={styles.pageListStats}>
                          {allCategoryItems > 0 &&
                            (approvedCategoryItems === allCategoryItems ? (
                              <span className={styles.pageListStatsAllApproved}>
                                {`All ${formatNumber(allCategoryItems)} Approved`}
                              </span>
                            ) : (
                              <>
                                <span
                                  className={
                                    approvedCategoryItems > 0
                                      ? styles.pageListStatsApprovedCount
                                      : ""
                                  }
                                >
                                  {formatNumber(approvedCategoryItems)}
                                </span>
                                {` of ${formatNumber(allCategoryItems)} approved`}
                              </>
                            ))}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outlinedIconSmall"
                      onClick={() => setIsApproveAllModalOpen(true)}
                      disabled={pendingApproveAll}
                    >
                      {pendingApproveAll ? (
                        <Spinner size="1rem" />
                      ) : (
                        <Icon
                          iconName={
                            allCategoryItemsApproved
                              ? "GreenCheckmarkIcon"
                              : "CloseIconCircled"
                          }
                          width="1rem"
                          height="1rem"
                        />
                      )}
                      {allCategoryItemsApproved
                        ? "All Approved"
                        : "I’m Feeling Lucky"}
                    </Button>
                    <ApproveAllConfirmationModal
                      isOpen={isApproveAllModalOpen}
                      onClose={() => setIsApproveAllModalOpen(false)}
                      isAllApproved={allCategoryItemsApproved}
                      toggleApprove={async () => {
                        await approveAsync({
                          kind: allCategoryItemsApproved
                            ? "unapprove"
                            : "approve",
                          variant: "automation",
                        });
                      }}
                      isApproveLoading={isApprovePending}
                      autoApprovalField="recommendations_auto_approval"
                    />
                  </div>
                  <div className={styles.pageListSearch}>
                    <Select
                      data={searchPages || []}
                      searchable
                      clearable
                      placeholder="Search"
                      onSearchChange={onSearchChange}
                      searchValue={inputValue}
                      isSearch={true}
                      isLoading={isLoadingSearchPages}
                      onChange={(_value, item) => {
                        setSelectedPageId(item?.value || null);
                        setUserRemovedSlash(false);
                        setCurrentCategoryId(undefined);
                      }}
                    />
                  </div>

                  {noData ? (
                    <NoData title="No Automations" />
                  ) : (
                    <>
                      {displayCategories.map((category) => (
                        <div
                          className={`${styles.pageListItem} ${
                            currentCategoryId === category.id
                              ? styles.pageItemActive
                              : ""
                          }`}
                          key={category.id}
                          onClick={() => handleCategoryClick(category)}
                        >
                          {/*
                          // @ts-expect-error TS2339 non-existing className */}
                          <div className={styles.categoryItemTitle}>
                            {category.category_name}
                          </div>
                          {category.approved_appearances !==
                          category.appearances ? (
                            // @ts-expect-error TS2339 non-existing className
                            <div className={styles.categoryItemStatus}>
                              {/*
                              // @ts-expect-error TS2551 non-existing className */}
                              <span className={styles.categoryItemApproved}>
                                {formatNumber(category.approved_appearances)} /{" "}
                                {formatNumber(category.appearances)}
                              </span>
                            </div>
                          ) : (
                            <Icon
                              iconName="GreenCheckmarkIcon"
                              width="1rem"
                              height="1rem"
                            />
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>

              <div className={styles.mainContentFooter}>
                <Button
                  disabled={
                    isAnyFetching ||
                    isApprovePending ||
                    allPagesLoaded ||
                    !!pendingStatusesAndPermissionsPageType?.pendingItems
                      ?.length
                  }
                  variant="outlinedIconSmall"
                  onClick={() => {
                    if (
                      pagination.currentPage < (pagination?.totalPages ?? 0)
                    ) {
                      setIsLoadMoreClicked(true);
                      setPagination({
                        ...pagination,
                        currentPage: pagination.currentPage + 1,
                      });
                    }
                  }}
                >
                  {isAnyFetching && isLoadMoreClicked && (
                    <Spinner size="1rem" />
                  )}
                  {allPagesLoaded ? "All Pages Loaded" : "Load More"}
                </Button>
              </div>
            </div>

            <div className={styles.pageItemList}>
              <>
                <div className={styles.pageItemListWrapper}>
                  <div className={styles.pageItemTitle}>
                    {currentCategory && (
                      <div>
                        <div className={styles.pageContainerHeader}>
                          <Switch
                            checked={showInactive}
                            withText={{ on: "Show All", off: "Show New" }}
                            onChange={(e) =>
                              toggleShowInactive(e.target.checked)
                            }
                          />
                          <h1 className={styles.pageContainerHeaderTitle}>
                            {currentCategory.category_name}
                          </h1>
                          <Button
                            variant="outlinedIconSmall"
                            disabled={pendingApproveAll}
                            onClick={handleApproveAll}
                          >
                            {pendingApproveAll ? (
                              <Spinner size="1rem" />
                            ) : (
                              <Icon
                                iconName={
                                  allApproved
                                    ? "GreenCheckmarkIcon"
                                    : "CloseIconCircled"
                                }
                                width="1rem"
                                height="1rem"
                              />
                            )}
                            {allApproved ? "Approved" : "Approve All"}
                          </Button>
                        </div>
                        {currentCategory.appearances &&
                          currentCategory.appearances > 0 && (
                            <div className={styles.pageContainerHeaderApproved}>
                              {allApproved ? (
                                <span>
                                  {formatNumber(
                                    currentCategory.approved_appearances,
                                  )}{" "}
                                  approved
                                </span>
                              ) : (
                                <span>
                                  {formatNumber(
                                    currentCategory.approved_appearances,
                                  )}{" "}
                                  of {formatNumber(currentCategory.appearances)}{" "}
                                  approved
                                </span>
                              )}
                            </div>
                          )}
                        {currentCategory.description && (
                          <p className={styles.pageContainerDescription}>
                            {currentCategory.description}
                          </p>
                        )}
                      </div>
                    )}

                    {isPageCategory ? (
                      currentCategory ? (
                        <CategoryPageItems
                          editPageSuggestion={editPageSuggestion}
                          currentCategory={currentCategory}
                          isDetailsLoading={isPagesTypeLoading}
                          approve={approve}
                          data={allData}
                          isPagesTypeLoading={isPagesTypeLoading}
                          isApprovePending={isApprovePending}
                          isWidgetActionLoading={isAnyFetching}
                          approvePermission={
                            pendingStatusesAndPermissionsPageType?.approvePermission ||
                            true
                          }
                          // @ts-expect-error BE types
                          pendingStatuses={
                            pendingStatusesAndPermissionsPageType?.pendingItems ||
                            []
                          }
                          isFetchingPendingStatusesAndPermissionsPageType={
                            isAnyFetching
                          }
                          fetchDetails={fetchPageDetails}
                        />
                      ) : null
                    ) : (
                      <CategoryAssetItems
                        isFetchingAssetSuggestions={isAnyFetching}
                        isLoading={isAnyFetching}
                        data={assetTypeData.map((item) => ({
                          ...item,
                          lang: item.lang ?? null,
                          appearances: item.appearances ?? 0,
                        }))}
                        currentCategory={currentCategory!}
                        approve={approve}
                        editPageSuggestion={editPageSuggestion}
                        isWidgetActionLoading={isAnyFetching}
                        // @ts-expect-error BE types
                        pendingStatuses={
                          pendingStatusesAndPermissionsPageType?.pendingItems ||
                          []
                        }
                      />
                    )}
                  </div>
                  {/*
                   // @ts-expect-error TS2339 non-existing className */}
                  <div ref={bottomRef} className={styles.bottomAnchor} />
                </div>
              </>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OnPage;
